import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslations } from "@/hooks/useTranslations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Channel } from './types';

type ChannelParticipantsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: Channel | null;
};

type Participant = {
  id: string; // user_id
  name: string;
  avatar_url?: string | null;
  role: string; // 'owner' or 'member'
};

export const ChannelParticipantsDialog = ({ open, onOpenChange, channel }: ChannelParticipantsDialogProps) => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslations('messages');

  useEffect(() => {
    if (!channel || !open) {
      setParticipants([]);
      return;
    }

    const fetchParticipants = async () => {
      setLoading(true);
      try {
        const { data: membersData, error: membersError } = await supabase
          .from('channel_members')
          .select('user_id, role')
          .eq('channel_id', channel.id);

        if (membersError) throw membersError;
        if (!membersData || membersData.length === 0) {
          setParticipants([]);
          return;
        }

        const userIds = membersData.map(m => m.user_id);

        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url')
          .in('id', userIds);

        if (profilesError) throw profilesError;

        const participantsList: Participant[] = membersData.map(member => {
          const profile = profilesData?.find(p => p.id === member.user_id);
          return {
            id: member.user_id,
            role: member.role,
            name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : t('unknownUser') || 'Utilizador Desconhecido',
            avatar_url: profile?.avatar_url
          };
        });
        
        participantsList.sort((a, b) => (a.role === 'owner' ? -1 : b.role === 'owner' ? 1 : 0));
        setParticipants(participantsList);

      } catch (error) {
        console.error('Error fetching participants:', error);
        toast.error(t('errorLoadingParticipants') || 'Erro ao carregar participantes.');
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [channel, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>{t('channelParticipants')}</DialogTitle>
          {channel && <DialogDescription>#{channel.name}</DialogDescription>}
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] mt-4 mb-4">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">{t('loading')}</div>
          ) : participants.length > 0 ? (
            <div className="space-y-3 pr-2">
              {participants.map((participant) => (
                <div key={participant.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={participant.avatar_url || ''} />
                      <AvatarFallback>
                        {participant.name?.charAt(0)?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm">{participant.name}</span>
                  </div>
                  {participant.role === 'owner' && (
                    <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      {t('owner')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              {t('noParticipantsFound')}
            </div>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 